import { FUNDING_TYPES, FUNDING_TYPE_SHORT_NAMES } from 'universal'
import { actionTypes } from '../../constants'
import { MessageFormat } from '@paypalcorp/worldready'
import { getNetworkStatus, isValidIntent } from 'utils/utils'
import { getQueryParam } from 'utils/query'
import { PRODUCT_NAMES, OPERATIONS } from 'universal/cfs-card'
import { REPLACE_CARD_REASONS } from '@/constants/card'
import { FLOW_STEPS } from '@/constants/flow-steps'

const { BUSINESS_DEBIT_CARD } = PRODUCT_NAMES
const { LOST_OR_STOLEN } = REPLACE_CARD_REASONS
const { REPORT_CARD_LOST, REISSUE } = OPERATIONS
const { CHOOSE_ADDRESS_STEP, NORMALIZED_ADDRESS_STEP, INVALID_ADDRESS_STEP, ADD_ADDRESS_STEP } =
  FLOW_STEPS.CHANGE_ADDRESS
const { SET_PIN_STEP } = FLOW_STEPS.SET_PIN

const {
  CANCEL_CARD,
  CHANGE_PIN,
  VIEW_PIN,
  CHANGE_CVV,
  SET_FUNDING,
  REMOVE_FUNDING,
  CREATE_CARD,
  APPLY_CARD,
  FETCH_ADDRESSES,
  FETCH_CARDS,
  FETCH_CARD,
  SHOW_CLASSIFIED_CARD_DATA,
  FETCH_FUNDING_SUMMARY,
  FETCH_FUNDING_DETAILS,
  REACTIVATE_CARD,
  DEACTIVATE_CARD,
} = actionTypes

const generic = 'generic'
const noInternet = 'noInternet'

// eslint-disable-next-line complexity
function getActionName(actionType) {
  let contentId
  switch (actionType) {
    case CANCEL_CARD:
      contentId = 'cancelCard'
      break
    case CHANGE_PIN:
      contentId = 'changePin'
      break
    case VIEW_PIN:
      contentId = 'viewPin'
      break
    case CHANGE_CVV:
      contentId = 'changeCvv'
      break
    case SET_FUNDING:
      contentId = 'setFunding'
      break
    case REMOVE_FUNDING:
      contentId = 'removeFunding'
      break
    case CREATE_CARD:
      contentId = 'createCard'
      break
    case APPLY_CARD:
      contentId = 'applyCard'
      break
    case FETCH_ADDRESSES:
      contentId = 'fetchAddresses'
      break
    case FETCH_CARDS:
      contentId = 'fetchCards'
      break
    case FETCH_CARD:
      contentId = 'fetchCard'
      break
    case SHOW_CLASSIFIED_CARD_DATA:
      contentId = 'showClassifiedCardData'
      break
    case FETCH_FUNDING_SUMMARY:
      contentId = 'fetchFundingSummary'
      break
    case FETCH_FUNDING_DETAILS:
      contentId = 'fetchFundingDetails'
      break
    case DEACTIVATE_CARD:
      contentId = 'lock'
      break
    case REACTIVATE_CARD:
      contentId = 'unlock'
      break
    default:
      contentId = generic
  }
  return contentId
}

function hasNonGenericContentKey(actionType) {
  return [
    CANCEL_CARD,
    CHANGE_PIN,
    VIEW_PIN,
    CHANGE_CVV,
    SET_FUNDING,
    REMOVE_FUNDING,
    CREATE_CARD,
  ].includes(actionType)
}

function getRetryContentId(type, actionType) {
  const isOffline = !getNetworkStatus()

  let contentId = generic
  if (isOffline) {
    contentId = noInternet
  } else if (hasNonGenericContentKey(actionType)) {
    contentId = getActionName(actionType)
  }
  return `error.retry.${contentId}.${type}`
}

function getRetryErrorContentId(type, actionType) {
  const contentId = hasNonGenericContentKey(actionType) ? getActionName(actionType) : generic
  return `error.retryError.${contentId}.${type}`
}

function getFundingOptionTypeContentId(fundingOptionType) {
  let contentId
  if (Object.keys(FUNDING_TYPES.ATU).includes(fundingOptionType)) {
    contentId = FUNDING_TYPE_SHORT_NAMES.ATU
  } else if (Object.keys(FUNDING_TYPES.BUFS).includes(fundingOptionType)) {
    contentId = FUNDING_TYPE_SHORT_NAMES.BUFS
  } else {
    contentId = generic
  }
  return `common.link.funding.${contentId}`
}

// Check for unlocalized string, e.g. [[Back]] or common.nav.dev
function isContentLocalized(string) {
  return !string.match(/^\[\[[\s\S]*]]$/) || string.match(/^\w+(\.\w+)*$/)
}

function getContentForWindowPromptFallbackForCopy(props) {
  const { worldReady = {}, shortProductName = '' } = props
  return {
    // message for window.prompt fallback
    message: new MessageFormat(worldReady, 'showCard.cardDetails.copyManually'.split('.')).format({
      shortProductName,
      // copy-to-clipboard will replace this with ⌘/Ctrl+C
      copyShortcutPlaceholder: '#{key}',
    }),
  }
}

function getContentIdsByReplaceCardType({
  isPayrollProviderFlow,
  isDigitalOnlyCard,
  pageType,
  productName = '',
}) {
  let contentIds
  let titleId = ''
  switch (pageType) {
    case REISSUE:
      if (isPayrollProviderFlow) {
        titleId = 'replaceCard.reissueCardDigitalOnly.payrollProvider.title'
      }
      contentIds = {
        header: isDigitalOnlyCard
          ? 'replaceCard.reissueCardDigitalOnly.header'
          : 'replaceCard.replaceDamaged.header',
        headerAlign: 'center',
        address: isDigitalOnlyCard ? '' : 'replaceCard.addressPanel.sendCardTo',
        button: isDigitalOnlyCard
          ? 'replaceCard.button.getPhysicalCard'
          : 'replaceCard.button.sendReplacementCard',
        title:
          isDigitalOnlyCard && productName === BUSINESS_DEBIT_CARD
            ? 'replaceCard.reissueCardDigitalOnly.bdmc.title'
            : titleId,
      }
      break
    case REPORT_CARD_LOST:
      const reason = getQueryParam('reason')
      let headerMessageId = 'replaceCard.reportLostStolen.header'
      let headerAlign = 'center'
      let bodyMessageId = 'replaceCard.reportLostStolen.content'
      let buttonMessageId = 'replaceCard.button.sendReplacementCard'
      if (isDigitalOnlyCard) {
        if (productName === BUSINESS_DEBIT_CARD && reason === LOST_OR_STOLEN) {
          headerMessageId = 'replaceCard.reportStolenDigitalOnly.bdmc.header'
          bodyMessageId = 'replaceCard.reportStolenDigitalOnly.bdmc.content'
          buttonMessageId = 'replaceCard.button.bdmc.reportStolenDigitalOnly'
        } else {
          headerMessageId = 'replaceCard.reportStolenDigitalOnly.header'
          bodyMessageId = 'replaceCard.reportStolenDigitalOnly.content'
          buttonMessageId = 'replaceCard.button.reportStolenDigitalOnly'
          titleId = 'replaceCard.reportStolenDigitalOnly.modalTitle'
        }
      }
      contentIds = {
        header: headerMessageId,
        headerAlign,
        body: bodyMessageId,
        button: buttonMessageId,
        title: titleId,
      }
      break
    default:
      contentIds = {
        title: titleId,
      }
  }
  return contentIds
}

function getContentIdsBySetPinPageType(pageType, productName = '', isFlowForRequestPhysicalCard = false) {
  let contentIds
  switch (pageType) {
    case SET_PIN_STEP:
      const contentIdPrefix = `setPin.createPin${
        productName === BUSINESS_DEBIT_CARD ? '.bdmc' : ''
      }` 
      
      contentIds = {
        modalTitle: `${contentIdPrefix}.modalTitle`,
        header: !isFlowForRequestPhysicalCard ? 'changePin.changePin.header' : `${contentIdPrefix}.header`,
        description: `${contentIdPrefix}.description`,
      }
      break
    default:
      contentIds = {}
  }
  return contentIds
}

function getContentIdsByAddressAndPageType(type, pageType, productName = '') {
  if (!type) return ''

  let contentIdPrefix
  let contentIds
  let titleContent = ''
  const addressType = type.toLowerCase()
  const intent = getQueryParam('intent')

  switch (pageType) {
    case CHOOSE_ADDRESS_STEP:
      contentIdPrefix = `changeAddress.chooseAddress.${addressType}`
      const headerId = `${contentIdPrefix}${isValidIntent(intent) ? `.${intent}` : ''}.header`
      titleContent =
        productName === BUSINESS_DEBIT_CARD
          ? 'changeAddress.chooseAddress.requestPhysicalCard.modalTitle'
          : `${contentIdPrefix}.modalTitle`
      contentIds = {
        header:
          productName === BUSINESS_DEBIT_CARD
            ? 'changeAddress.chooseAddress.requestPhysicalCard.header'
            : headerId,
        addAddress: `changeAddress.link.${addressType}.addAddress`,
        save: 'changeAddress.button.save',
        confirm: 'changeAddress.button.confirm',
        modalTitle: titleContent,
      }
      break
    case NORMALIZED_ADDRESS_STEP:
      contentIdPrefix = `changeAddress.normalizedAddress.${addressType}`
      contentIds = {
        header: `${contentIdPrefix}.header`,
        matchedAddresses: `${contentIdPrefix}.matchedAddresses`,
        enteredAddress: `${contentIdPrefix}.enteredAddress`,
        editAddress: 'changeAddress.link.editAddress',
        save: 'changeAddress.button.save',
        modalTitle: `${contentIdPrefix}.modalTitle`,
      }
      break
    case INVALID_ADDRESS_STEP:
      contentIdPrefix = `changeAddress.invalidAddress.${addressType}`
      contentIds = {
        header: `${contentIdPrefix}.header`,
        addAddress: `changeAddress.button.addANewAddress`,
        useThisAddress: 'changeAddress.button.useThisAddress',
        modalTitle: `${contentIdPrefix}.modalTitle`,
      }
      break
    case ADD_ADDRESS_STEP:
      contentIdPrefix = `changeAddress.addAddress.${addressType}`
      titleContent =
        productName === BUSINESS_DEBIT_CARD
          ? 'changeAddress.addAddress.requestPhysicalCard.modalTitle'
          : `${contentIdPrefix}.modalTitle`
      contentIds = {
        modalTitle: titleContent,
      }
      break
    default:
      contentIds = {
        modalTitle: titleContent,
      }
  }
  return contentIds
}

function getMessageFormatter(worldReady) {
  return function (key, options = {}) {
    if (!worldReady || !key) return ''

    return new MessageFormat(worldReady, key.split('.')).format(options)
  }
}

const getFormatMessageObject = (id, variables = {}) => {
  return {
    id,
    variables,
  }
}

const formatContentMessage = (contentKey, worldReady) => {
  const formatMessage = getMessageFormatter(worldReady)
  return formatMessage(contentKey)
}

//Create content message id by appending countrycode followed by '_'
const formatCountrySpecificContentId = (contentId, countryCode) => {
  const start = contentId.substring(0, contentId.lastIndexOf('.') + 1)
  const last = contentId.substring(contentId.lastIndexOf('.') + 1, contentId.length)

  return `${start}${countryCode}_${last}`
}

export {
  getActionName,
  getRetryContentId,
  getRetryErrorContentId,
  getFundingOptionTypeContentId,
  isContentLocalized,
  getContentForWindowPromptFallbackForCopy,
  getMessageFormatter,
  getContentIdsByAddressAndPageType,
  getContentIdsBySetPinPageType,
  getFormatMessageObject,
  formatContentMessage,
  formatCountrySpecificContentId,
  getContentIdsByReplaceCardType,
}
